import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "../scripts/AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { fetchPlanCatalog, markPendingTour } from "@/lib/billingApi";
import PlanCatalogPicker from "@/components/PlanCatalogPicker";
import MockCheckoutPanel from "@/components/MockCheckoutPanel";
import { cn } from "@/lib/utils";
import { toastSuccess, toastError } from "@/lib/toast";

const STEPS = ["basics", "plan", "sso", "invites", "checkout"];
const STEP_LABELS = {
  basics: "Basics",
  plan: "Plan",
  sso: "SSO",
  invites: "Invites",
  checkout: "Checkout",
};

const SSO_PROVIDERS = [
  {
    id: "entra",
    name: "Microsoft Entra ID",
    blurb: "Common for Microsoft 365 workplaces",
  },
  {
    id: "okta",
    name: "Okta",
    blurb: "Popular enterprise identity cloud",
  },
  {
    id: "google",
    name: "Google Workspace",
    blurb: "Sign in with Google work accounts",
  },
  {
    id: "onelogin",
    name: "OneLogin",
    blurb: "Workforce identity platform",
  },
  {
    id: "other",
    name: "Other SAML / OIDC",
    blurb: "Any standards-based identity provider",
  },
];

/**
 * Multi-step create-organization wizard. Nothing is created until Finish.
 */
export default function CreateOrganizationWizard({
  open,
  onOpenChange,
  onCreated,
}) {
  const { accessToken } = useAuth();
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ownerOrgEmail, setOwnerOrgEmail] = useState("");
  const [plan, setPlan] = useState("free");
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const [inviteDraft, setInviteDraft] = useState("");
  const [inviteEmails, setInviteEmails] = useState([]);

  const visibleSteps = useMemo(() => {
    if (plan === "free") {
      return STEPS.filter((s) => s !== "checkout");
    }
    return STEPS;
  }, [plan]);

  const stepKey = visibleSteps[step] || "basics";
  const catalogEntry = catalog.find((c) => c.plan === plan);
  const ssoIncluded = !!catalogEntry?.modules?.sso;

  const reset = () => {
    setStep(0);
    setError(null);
    setSubmitting(false);
    setName("");
    setDescription("");
    setOwnerOrgEmail("");
    setPlan("free");
    setCheckoutSessionId(null);
    setInviteDraft("");
    setInviteEmails([]);
  };

  useEffect(() => {
    if (!open) return;
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !accessToken) return;
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const rows = await fetchPlanCatalog(accessToken);
        if (!cancelled) setCatalog(rows);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              "Could not load plan catalog"
          );
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accessToken]);

  // When plan changes away from paid, clear checkout; clamp step index
  useEffect(() => {
    if (plan === "free") setCheckoutSessionId(null);
    setStep((s) => Math.min(s, Math.max(0, visibleSteps.length - 1)));
  }, [plan, visibleSteps.length]);

  const validateStep = () => {
    if (stepKey === "basics") {
      if (!name.trim()) {
        setError("Organization name is required");
        return false;
      }
      if (!ownerOrgEmail.trim() || !ownerOrgEmail.includes("@")) {
        setError("A valid contact email is required");
        return false;
      }
    }
    if (stepKey === "plan") {
      if (!plan || plan === "enterprise") {
        setError("Select Free, Pro, or Business");
        return false;
      }
    }
    if (stepKey === "checkout") {
      if (!checkoutSessionId) {
        setError("Complete the demo checkout to continue");
        return false;
      }
    }
    setError(null);
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, visibleSteps.length - 1));
  };

  const goBack = () => {
    setError(null);
    setStep((s) => Math.max(s - 1, 0));
  };

  const addInviteEmail = () => {
    const e = inviteDraft.trim().toLowerCase();
    if (!e || !e.includes("@")) {
      setError("Enter a valid email to invite");
      return;
    }
    if (inviteEmails.includes(e)) {
      setInviteDraft("");
      return;
    }
    setInviteEmails((prev) => [...prev, e].slice(0, 50));
    setInviteDraft("");
    setError(null);
  };

  const handleFinish = async () => {
    if (!validateStep() || submitting) return;
    if (plan !== "free" && !checkoutSessionId) {
      setError("Complete the demo checkout to continue");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        plan,
        owner_org_email: ownerOrgEmail.trim().toLowerCase(),
        invite_emails: inviteEmails.length ? inviteEmails : undefined,
      };
      if (plan !== "free") {
        body.checkout_session_id = checkoutSessionId;
      }
      const res = await axios.post(`${API_BASE_URL}/org/create`, body, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const org = res.data;
      // Trust what the user actually queued in the wizard — empty list means no invite toast.
      const requested = inviteEmails.length;
      const failed = Array.isArray(org?.invite_summary?.failed)
        ? org.invite_summary.failed.length
        : 0;
      const sent = Number(org?.invite_summary?.sent) || 0;
      if (!requested) {
        toastSuccess("Organization created");
      } else if (failed && sent) {
        toastError(
          `Org created. Sent ${sent} invite(s); ${failed} failed.`
        );
      } else if (failed && !sent) {
        toastError("Org created, but invites could not be sent.");
      } else if (sent) {
        toastSuccess(`Organization created. ${sent} invite(s) sent.`);
      } else {
        toastSuccess("Organization created");
      }
      if (org?.org_id && org?.start_workspace_tour) {
        markPendingTour(org.org_id);
      }
      onOpenChange?.(false);
      await onCreated?.(org);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message;
      setError(
        Array.isArray(msg)
          ? msg.join(", ")
          : msg || err.message || "Failed to create organization"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isLast = step === visibleSteps.length - 1;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submitting) return;
        onOpenChange?.(next);
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create workspace</DialogTitle>
          <DialogDescription>
            Set up your workspace. Nothing is created until you finish.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5 pb-2">
          {visibleSteps.map((key, i) => (
            <Badge
              key={key}
              variant={i === step ? "default" : "outline"}
              className="text-[10px] font-normal"
            >
              {i + 1}. {STEP_LABELS[key]}
            </Badge>
          ))}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-2">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {stepKey === "basics" && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="create-org-name">Name *</Label>
              <Input
                id="create-org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Organization name"
              />
            </div>
            <div>
              <Label htmlFor="create-org-desc">Description</Label>
              <textarea
                id="create-org-desc"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div>
              <Label htmlFor="create-org-email">Your contact email *</Label>
              <Input
                id="create-org-email"
                type="email"
                value={ownerOrgEmail}
                onChange={(e) => setOwnerOrgEmail(e.target.value)}
                placeholder="you@company.com"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Shown as your org profile contact. Required for the owner.
              </p>
            </div>
          </div>
        )}

        {stepKey === "plan" && (
          <div className="space-y-3">
            {catalogLoading ? (
              <p className="text-sm text-muted-foreground">Loading plans…</p>
            ) : (
              <PlanCatalogPicker
                catalog={catalog}
                selectedPlan={plan}
                onSelect={setPlan}
                disabled={submitting}
              />
            )}
            <p className="text-xs text-muted-foreground">
              Enterprise is contact sales only. Limits and prices come from
              Circa’s live plan templates.
            </p>
          </div>
        )}

        {stepKey === "checkout" && (
          <MockCheckoutPanel
            accessToken={accessToken}
            plan={plan}
            catalogEntry={catalogEntry}
            confirmationEmail={ownerOrgEmail}
            disabled={submitting}
            onSucceeded={(data) => {
              setCheckoutSessionId(data.session_id);
              setError(null);
            }}
          />
        )}

        {stepKey === "sso" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Optional. Keep email login, or connect an identity provider (IdP)
              like Microsoft Entra ID or Okta so people sign in with work
              accounts. Useful for larger orgs and IT reviews — you can skip and
              add this later.
            </p>
            {!ssoIncluded && (
              <Alert>
                <AlertDescription>
                  SSO is included on Business and Enterprise. Your current plan
                  selection does not include it — upgrade to Business to unlock
                  later, or continue without SSO.
                </AlertDescription>
              </Alert>
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              {SSO_PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={!ssoIncluded || submitting}
                  onClick={() =>
                    toastSuccess(
                      "Self-serve IdP setup is not available yet — Circa can configure SSO for Business and Enterprise workplaces. Continue without connecting an IdP here."
                    )
                  }
                  className={cn(
                    "rounded-lg border border-border p-3 text-left text-sm",
                    ssoIncluded
                      ? "hover:border-primary/40"
                      : "cursor-not-allowed opacity-60"
                  )}
                >
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.blurb}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {stepKey === "invites" && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Optionally invite people by email. You can skip and invite later.
              (Later, directory sync from your IdP can help populate this.)
            </p>
            <div className="flex flex-wrap gap-2">
              {inviteEmails.map((e) => (
                <Badge key={e} variant="secondary" className="gap-1 pr-1">
                  {e}
                  <button
                    type="button"
                    className="ml-1 rounded px-1 text-xs hover:bg-muted"
                    onClick={() =>
                      setInviteEmails((prev) => prev.filter((x) => x !== e))
                    }
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={inviteDraft}
                onChange={(e) => setInviteDraft(e.target.value)}
                placeholder="colleague@company.com"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addInviteEmail();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={addInviteEmail}>
                Add
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          {step > 0 && (
            <Button
              type="button"
              variant="ghost"
              onClick={goBack}
              disabled={submitting}
            >
              Back
            </Button>
          )}
          {!isLast ? (
            <Button type="button" onClick={goNext} disabled={submitting}>
              Next
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleFinish}
              disabled={submitting}
            >
              {submitting ? "Creating…" : "Create workspace"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
